import { BreadcrumbList } from '@/components/Breadcrumbs/BreadcrumbList';
import { BreadcrumbItem } from '@/components/Breadcrumbs/BreadcrumbItem';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useCreateUser } from '@/hooks/user.hooks';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { UserProfileManagement } from '../components/UserProfileManagement';
import { useNavigate } from 'react-router-dom';
import '../styles/manage-user.styles.css';
import {
  registerUserSchema,
  TRegisterUserSchema,
} from '@/schemas/user/register-user.schema';
import { useAuth, useUserRole } from '@/hooks/auth.hooks';
import { Loader } from '@/components/Loading/Loader';
import { useEffect } from 'react';

export const CreateUserPage = () => {
  const { t } = useTranslation('user');
  const navigate = useNavigate();
  const { loggedInUser } = useAuth();
  const { hasNoRole } = useUserRole();

  useEffect(() => {
    if (hasNoRole) {
      navigate('/');
    }
  }, []);

  const {
    watch: watchUser,
    control: userControl,
    register: registerUser,
    setValue: setUserValue,
    setError: setUserError,
    handleSubmit: handleSubmitUser,
    formState: { errors: userErrors, isSubmitting: isSubmittingUser },
  } = useForm<TRegisterUserSchema>({
    resolver: zodResolver(registerUserSchema),
  });

  const { mutate: mutateCreateUser, isPending: isPendingCreateUser } =
    useCreateUser({
      t,
      handleSuccess: () => navigate('/users'),
    });

  const onSubmitUser = (data: TRegisterUserSchema) => {
    delete data?.passwordConfirm;
    data.createdBy = loggedInUser?.id;
    mutateCreateUser(data);
  };

  return (
    <>
      <PageHeader
        backToLink="/users"
        title={t('common:user')}
        headingElement={
          <BreadcrumbList>
            <BreadcrumbItem text={t('userList')} linkTo="/users" />
            <BreadcrumbItem text={t('common:account')} active />
          </BreadcrumbList>
        }
      />
      <div className="flex flex-col gap-12">
        <UserProfileManagement<TRegisterUserSchema>
          t={t}
          watch={watchUser}
          control={userControl}
          register={registerUser}
          setValue={setUserValue}
          setError={setUserError}
          errors={userErrors}
          submitDisabled={isSubmittingUser || isPendingCreateUser}
          onSubmit={handleSubmitUser(onSubmitUser)}
        />
      </div>
      {(isSubmittingUser || isPendingCreateUser) && <Loader />}
    </>
  );
};
